/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError, AxiosRequestConfig } from 'axios';
import { auth } from '../../auth/firebase';
import { signInWithEmailAndPassword, User } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import { apiCall } from 'src/lib/utils/api.util';
import { COOKIE_KEYS, FORBIDDEN_STATUS_CODE, UNAUTHORIZED_STATUS_CODE } from 'src/lib/constants';
import { isValidJsonString } from 'src/lib/utils/string.util';
import { setJwtToken } from 'src/lib/utils/auth.util';
import { VITALY_PARTNER_CODE } from 'src/lib/utils/vitaly.util';
import { captureLabeledSentryError, SENTRY_LABEL } from 'src/lib/utils/error.util';

enum VitalyKeysAction {
  VITALY = 'VITALY',
  CHILD_LOADED = 'CHILD_LOADED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  CLOSE = 'CLOSE',
  FORBIDDEN = 'FORBIDDEN',
  MENU = 'MENU',
  CLIPBOARD = 'CLIPBOARD',
  DOWNLOAD = 'DOWNLOAD',
  BLOB = 'BLOB',
}

interface VitalyFrameMessage {
  key: VitalyKeysAction;
  type?: VitalyKeysAction;
  token?: string;
  callback?: string;
  clipboard?: string;
  data?: any;
  floatingMenuParams?: FloatingMenuParams;
}

enum FloatingMenuPosition {
  TopLeft = 'top-left',
  TopCenter = 'top-center',
  TopRight = 'top-right',
  CenterLeft = 'center-left',
  CenterCenter = 'center-center',
  CenterRight = 'center-right',
  BottomLeft = 'bottom-left',
  BottomCenter = 'bottom-center',
  BottomRight = 'bottom-right',
}

interface FloatingMenuParams {
  position: FloatingMenuPosition;
  showBackButton: boolean;
}

interface VitalyRequestCallback {
  path: string;
  requestUrl: string;
}

let refetchRequests: AxiosRequestConfig[] = [];
let isProcessingMessage = false;

/**
 * Listens for Vitaly messages and performs necessary actions based on the received data.
 * @param e - The MessageEvent object containing the data received from Vitaly.
 */
export const vitalyMessagesListener = async (e: MessageEvent) => {
  if (!isProcessingMessage) {
    isProcessingMessage = true;
    try {
      const data = isValidJsonString(e.data) ? JSON.parse(e.data) : e.data;
      console.log('Vitaly message recived', data);
      if (data.key === VitalyKeysAction.VITALY) {
        if (!localStorage.getItem('partner') || localStorage.getItem('partner') !== VITALY_PARTNER_CODE) {
          localStorage.setItem('partner', VITALY_PARTNER_CODE);
        }
        const message: VitalyFrameMessage = { ...data };
        if (message.token) {
          localStorage.setItem('vitalyToken', message.token);
          const payload = jwtDecode<any>(message.token);
          const userCredential = await signInWithEmailAndPassword(
            auth,
            payload.email,
            `000000${payload.vitaly_user_id}`,
          );
          const fisifyToken = await userCredential.user.getIdToken();
          setJwtToken(fisifyToken);
          await handleRefetchRequests(message);
        }
      }
    } finally {
      isProcessingMessage = false;
    }
  }
};

const handleRefetchRequests = async (message: VitalyFrameMessage) => {
  if (message.callback && isValidJsonString(message.callback)) {
    const callback: VitalyRequestCallback = JSON.parse(message.callback);
    const currentRequest = refetchRequests.find((request) => request.url === callback.requestUrl);
    if (currentRequest) {
      refetchRequests = refetchRequests.filter((request) => request.url !== callback.requestUrl);
      apiCall.request(currentRequest);
    }
  }
};

export const transformVitalyRequestHeaders = async (config: AxiosRequestConfig) => {
  const vitalyToken = localStorage.getItem('vitalyToken');
  const fisifyToken = Cookies.get(COOKIE_KEYS.Jwt) ?? localStorage.getItem(COOKIE_KEYS.Jwt);
  if (config.headers) {
    if (vitalyToken) config.headers.authorization = `Bearer ${vitalyToken}`;
    if (fisifyToken) config.headers['Fisify-Authorization'] = `Bearer ${fisifyToken}`;
  }

  return config;
};

export const getVitalyRequestHeaders = async (user: User | null) => {
  const headers: any = {};
  const vitalyToken = localStorage.getItem('vitalyToken');
  const fisifyToken =
    Cookies.get(COOKIE_KEYS.Jwt) ?? localStorage.getItem(COOKIE_KEYS.Jwt) ?? (await user?.getIdToken());
  if (vitalyToken) headers.authorization = `Bearer ${vitalyToken}`;
  if (fisifyToken) headers['Fisify-Authorization'] = `Bearer ${fisifyToken}`;

  return headers;
};

/**
 * Handles Vitaly service errors and performs necessary actions based on the error status.
 * @param error - The Axios error object.
 * @returns The original error object.
 */
export const handleVitalyError = (error: AxiosError) => {
  const originalRequest = error.config;
  let message: VitalyFrameMessage;
  captureLabeledSentryError(SENTRY_LABEL.VITALY, 'Error occurred on Vitaly implementation', {
    error,
  });
  if (error?.response?.status === UNAUTHORIZED_STATUS_CODE) {
    message = {
      key: VitalyKeysAction.VITALY,
      type: VitalyKeysAction.UNAUTHORIZED,
      callback: JSON.stringify({ path: window.location.pathname, requestUrl: originalRequest.url }),
    };
    sendMessageToParentApplication(message);
    refetchRequests.push(originalRequest);
  } else if (error?.response?.status === FORBIDDEN_STATUS_CODE) {
    message = { key: VitalyKeysAction.VITALY, type: VitalyKeysAction.CLOSE };
    sendMessageToParentApplication(message);
  }

  return error.config;
};

export const sendFloatingMenuMessage = () => {
  const message: VitalyFrameMessage = {
    key: VitalyKeysAction.VITALY,
    type: VitalyKeysAction.MENU,
    floatingMenuParams: { position: FloatingMenuPosition.BottomCenter, showBackButton: false },
  };
  sendMessageToParentApplication(message);
};

const sendMessageToParentApplication = (message: VitalyFrameMessage) => {
  window.parent.postMessage(message, '*');
};
